import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { IconArrowRight, IconClose } from './icons';

/**
 * Every floating surface in the application lives in this file, and they are the only
 * places translucency is used. Blur is earned: if a surface is not actually above the
 * content, it stays solid.
 */

const FOCUSABLE =
  'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea, [tabindex]:not([tabindex="-1"])';

/** Focus trap + Escape + Android/hardware back, shared by every dismissible layer. */
function useLayerBehaviour(
  open: boolean,
  onClose: () => void,
  panelRef: React.RefObject<HTMLElement>,
) {
  const closeRef = useRef(onClose);
  closeRef.current = onClose;

  useEffect(() => {
    if (!open) return;
    const previouslyFocused = document.activeElement as HTMLElement | null;
    panelRef.current?.focus();

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        closeRef.current();
        return;
      }
      if (event.key !== 'Tab') return;
      const focusables = panelRef.current?.querySelectorAll<HTMLElement>(FOCUSABLE);
      if (!focusables?.length) return;
      const first = focusables[0]!;
      const last = focusables[focusables.length - 1]!;
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', onKeyDown);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = previousOverflow;
      previouslyFocused?.focus?.();
    };
  }, [open, panelRef]);

  // Back closes the layer rather than leaving the screen. The entry is tagged so a
  // rewind can never undo a navigation the app made in the meantime.
  useEffect(() => {
    if (!open) return;
    const token = `layer-${Date.now()}`;
    history.pushState({ rvLayer: token }, '');
    const onPopState = () => closeRef.current();
    window.addEventListener('popstate', onPopState);
    return () => {
      window.removeEventListener('popstate', onPopState);
      if ((history.state as { rvLayer?: string } | null)?.rvLayer === token) history.back();
    };
  }, [open]);
}

/* ---------------------------------------------------------------- Sheet */

/**
 * One component, two presentations: a bottom sheet on a phone, a centred dialog from
 * 768px up. The title row and the scrolling body are separate boxes so a long form
 * scrolls under a title that stays put.
 */
export function Sheet({
  open,
  title,
  onClose,
  children,
  footer,
}: {
  open: boolean;
  title: string;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
}) {
  const panelRef = useRef<HTMLDivElement>(null);
  useLayerBehaviour(open, onClose, panelRef);

  if (!open) return null;

  return (
    <div
      className="scrim scrim-bottom no-print"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div
        className="sheet layer"
        role="dialog"
        aria-modal="true"
        aria-labelledby="sheet-title"
        ref={panelRef}
        tabIndex={-1}
      >
        <div className="sheet-grab" aria-hidden="true" />
        <div className="sheet-head">
          <h2 id="sheet-title" className="sheet-title">
            {title}
          </h2>
          <button className="icon-btn" onClick={onClose} aria-label="إغلاق">
            <IconClose size={17} />
          </button>
        </div>
        <div className="sheet-body">{children}</div>
        {footer && <div className="sheet-foot">{footer}</div>}
      </div>
    </div>
  );
}

/* -------------------------------------------------------------- Popover */

/**
 * Desktop-only anchored panel. On phones the caller uses a Sheet instead — a popover
 * pinned to a control is a pointer idiom and does not survive a thumb.
 */
export function Popover({
  open,
  onClose,
  children,
  labelledBy,
}: {
  open: boolean;
  onClose: () => void;
  children: ReactNode;
  labelledBy?: string;
}) {
  const panelRef = useRef<HTMLDivElement>(null);
  const closeRef = useRef(onClose);
  closeRef.current = onClose;

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        closeRef.current();
      }
    };
    const onPointerDown = (event: MouseEvent) => {
      if (!panelRef.current?.contains(event.target as Node)) closeRef.current();
    };
    document.addEventListener('keydown', onKeyDown);
    // `click` rather than `mousedown`, so the toggle button's own click still runs.
    document.addEventListener('click', onPointerDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('click', onPointerDown);
    };
  }, [open]);

  if (!open) return null;

  return (
    <div
      className="popover layer"
      role="dialog"
      aria-modal="false"
      aria-labelledby={labelledBy}
      ref={panelRef}
    >
      {children}
    </div>
  );
}

/* ------------------------------------------------------------- Lightbox */

export interface LightboxPhoto {
  id: string;
  section: string;
  sectionLabel: string;
  sequence: number;
  capturedAt: string;
  url: string | null;
}

/**
 * Full-bleed evidence viewer.
 *
 * The photo keeps its own proportions here as everywhere else — `object-fit: contain`
 * against the available box, never cropped to fill it. Controls float on a translucent
 * material over the image, which is exactly the case visionOS depth is for.
 */
export function Lightbox({
  photos,
  index,
  onClose,
  onNavigate,
  formatTime,
  labels,
}: {
  photos: LightboxPhoto[];
  index: number | null;
  onClose: () => void;
  onNavigate: (next: number) => void;
  formatTime: (iso: string) => string;
  labels: { close: string; previous: string; next: string; captured: string; of: string };
}) {
  const panelRef = useRef<HTMLDivElement>(null);
  const open = index !== null;
  useLayerBehaviour(open, onClose, panelRef);

  const go = useCallback(
    (delta: number) => {
      if (index === null) return;
      const next = index + delta;
      if (next < 0 || next >= photos.length) return;
      onNavigate(next);
    },
    [index, photos.length, onNavigate],
  );

  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => {
      // RTL: ArrowLeft advances, because "forward" is leftward in Arabic.
      const rtl = document.documentElement.dir === 'rtl';
      if (event.key === 'ArrowLeft') go(rtl ? 1 : -1);
      if (event.key === 'ArrowRight') go(rtl ? -1 : 1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, go]);

  if (index === null) return null;
  const photo = photos[index];
  if (!photo) return null;

  return (
    <div
      className="lightbox no-print"
      role="dialog"
      aria-modal="true"
      aria-label={`${photo.sectionLabel} ${photo.sequence}`}
      ref={panelRef}
      tabIndex={-1}
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div className="lightbox-bar layer">
        <div className="lightbox-meta">
          <span className="lightbox-section">{photo.sectionLabel}</span>
          <span className="lightbox-seq num" dir="ltr">
            {photo.sequence}
          </span>
          <span className="lightbox-sep" aria-hidden="true">
            ·
          </span>
          <span className="lightbox-time num">
            {labels.captured} {formatTime(photo.capturedAt)}
          </span>
        </div>
        <div className="lightbox-actions">
          <span className="lightbox-count num" dir="ltr">
            {index + 1} {labels.of} {photos.length}
          </span>
          <button className="icon-btn icon-btn-onlayer" onClick={onClose} aria-label={labels.close}>
            <IconClose size={18} />
          </button>
        </div>
      </div>

      <figure className="lightbox-stage">
        {photo.url ? (
          <img className="lightbox-image" src={photo.url} alt="" />
        ) : (
          <div className="lightbox-loading skeleton" />
        )}
      </figure>

      <div className="lightbox-nav">
        <button
          className="icon-btn icon-btn-onlayer layer"
          onClick={() => go(-1)}
          disabled={index === 0}
          aria-label={labels.previous}
        >
          <IconArrowRight size={18} className="nav-prev" />
        </button>
        <button
          className="icon-btn icon-btn-onlayer layer"
          onClick={() => go(1)}
          disabled={index === photos.length - 1}
          aria-label={labels.next}
        >
          <IconArrowRight size={18} className="nav-next" />
        </button>
      </div>
    </div>
  );
}

/** Tracks the viewport so a caller can choose a popover on desktop, a sheet on phone. */
export function useIsDesktop(minWidth = 1024): boolean {
  const [isDesktop, setIsDesktop] = useState(
    () => typeof window !== 'undefined' && window.innerWidth >= minWidth,
  );
  useEffect(() => {
    const query = window.matchMedia(`(min-width: ${minWidth}px)`);
    const update = () => setIsDesktop(query.matches);
    update();
    query.addEventListener('change', update);
    return () => query.removeEventListener('change', update);
  }, [minWidth]);
  return isDesktop;
}
